import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  ChartOptions,
  TooltipItem,
} from 'chart.js';
import { Radar } from 'react-chartjs-2';
import {
  RADAR_DIMENSION_KEYS,
  RadarDimensionKey,
  getRadarDimensionLabel,
  getRadarDimensionTooltipMeta,
} from '../../utils/radarDimensions';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

const CANDIDATE_COLORS = [
  { bg: 'rgba(59, 130, 246, 0.18)', border: 'rgba(59, 130, 246, 0.9)', point: 'rgba(59, 130, 246, 1)' },       // Blue
  { bg: 'rgba(16, 185, 129, 0.18)', border: 'rgba(16, 185, 129, 0.9)', point: 'rgba(16, 185, 129, 1)' },       // Emerald
  { bg: 'rgba(245, 158, 11, 0.18)', border: 'rgba(245, 158, 11, 0.9)', point: 'rgba(245, 158, 11, 1)' },       // Amber
  { bg: 'rgba(139, 92, 246, 0.18)', border: 'rgba(139, 92, 246, 0.9)', point: 'rgba(139, 92, 246, 1)' },       // Violet
  { bg: 'rgba(6, 182, 212, 0.18)', border: 'rgba(6, 182, 212, 0.9)', point: 'rgba(6, 182, 212, 1)' },          // Cyan
];

function wrapRadarLabel(label: string): string[] {
  const words = label.split(/\s+/).filter(Boolean);
  if (words.length <= 2) return [label];

  const lines: string[] = [];
  let current = '';
  const targetLength = 12;

  words.forEach(word => {
    const next = current ? `${current} ${word}` : word;
    if (next.length > targetLength && current) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  });

  if (current) lines.push(current);
  return lines.length > 1 ? lines : [label];
}

/* ── Types ── */
export interface CandidateRadarData {
  name: string;
  resumeFilename: string;
  score: number;
  dimensionScores: Record<string, number>;
}

interface DimensionRadarChartProps {
  dimensionWeights: Record<string, number>;
  candidates: CandidateRadarData[];
  displayResumeName: (f: string) => string;
  showCandidateControls?: boolean;
  forceShowAll?: boolean;
  showCopyAction?: boolean;
}

/* ── Helpers ── */
function normalizeWeightsToScale(weights: Record<string, number>): number[] {
  const vals = RADAR_DIMENSION_KEYS.map(k => weights[k] || 0);
  const maxW = Math.max(...vals, 0.001);
  return vals.map(v => (v / maxW) * 100);
}

/* ── Component ── */
const DimensionRadarChart: React.FC<DimensionRadarChartProps> = ({
  dimensionWeights,
  candidates,
  displayResumeName: displayName,
  showCandidateControls = true,
  forceShowAll = false,
  showCopyAction = true,
}) => {
  const chartRef = useRef<any>(null);
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(() => {
    const initial = new Set<number>();
    const top = forceShowAll ? Math.min(5, candidates.length) : Math.min(1, candidates.length);
    for (let i = 0; i < top; i++) initial.add(i);
    return initial;
  });

  // When forceShowAll, compute effective indices directly — no state update needed
  const effectiveIndices = forceShowAll
    ? new Set(Array.from({ length: Math.min(5, candidates.length) }, (_, i) => i))
    : selectedIndices;

  useEffect(() => {
    if (forceShowAll) return; // handled via effectiveIndices, no state update

    setSelectedIndices(prev => {
      const valid = new Set<number>();
      prev.forEach(i => {
        if (i >= 0 && i < candidates.length) valid.add(i);
      });

      if (valid.size > 0) return valid;
      if (candidates.length > 0) return new Set([0]);
      return valid;
    });
  }, [candidates.length, forceShowAll]);

  const toggleCandidate = useCallback((idx: number) => {
    setSelectedIndices(prev => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        if (next.size >= 5) return prev;   // cap at 5 overlays
        next.add(idx);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    const max = Math.min(5, candidates.length);
    setSelectedIndices(new Set(Array.from({ length: max }, (_, i) => i)));
  }, [candidates.length]);

  const clearAll = useCallback(() => {
    setSelectedIndices(new Set());
  }, []);

  /* ── Normalize JD weights so max weight → 100 (outer boundary) ── */
  const jdBoundary = normalizeWeightsToScale(dimensionWeights);
  const labels = RADAR_DIMENSION_KEYS.map(k => wrapRadarLabel(getRadarDimensionLabel(k, 'full')));

  /* ── Build Chart.js datasets ── */
  /* JD rendered as a filled "target zone" background — not a competing line */
  const datasets: any[] = [
    {
      label: 'Job Requirements',
      data: jdBoundary,
      backgroundColor: 'rgba(239, 68, 68, 0.10)',
      borderColor: 'rgba(239, 68, 68, 0.45)',
      borderWidth: 1.5,
      borderDash: [8, 4],
      pointBackgroundColor: 'rgba(239, 68, 68, 0.35)',
      pointBorderColor: 'transparent',
      pointBorderWidth: 0,
      pointRadius: 2.5,
      pointHoverRadius: 5,
      fill: true,
      order: 100,   // draw first (behind candidates)
    },
  ];

  let colorIdx = 0;
  const sortedCandidates = [...candidates]; // already sorted by score desc from parent

  sortedCandidates.forEach((c, i) => {
    if (!effectiveIndices.has(i)) return;
    const color = CANDIDATE_COLORS[colorIdx % CANDIDATE_COLORS.length];
    colorIdx++;
    datasets.push({
      label: displayName(c.resumeFilename),
      data: RADAR_DIMENSION_KEYS.map(k => c.dimensionScores[k] ?? 0),
      backgroundColor: color.bg,
      borderColor: color.border,
      borderWidth: 2,
      pointBackgroundColor: color.point,
      pointBorderColor: '#fff',
      pointBorderWidth: 1.5,
      pointRadius: 3.5,
      pointHoverRadius: 6,
      fill: true,
      order: 50 - colorIdx,  // later overlays draw on top
    });
  });

  const chartData = { labels, datasets };

  const options: ChartOptions<'radar'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      r: {
        min: 0,
        max: 100,
        ticks: {
          stepSize: 20,
          display: true,
          backdropColor: 'transparent',
          color: 'rgba(107, 114, 128, 0.6)',
          font: { size: 10 },
        },
        grid: {
          color: 'rgba(107, 114, 128, 0.15)',
          lineWidth: 1,
        },
        angleLines: {
          color: 'rgba(107, 114, 128, 0.15)',
          lineWidth: 1,
        },
        pointLabels: {
          font: { size: 10, weight: 'bold' as const },
          color: 'rgba(55, 65, 81, 0.9)',
          padding: 8,
        },
      },
    },
    plugins: {
      legend: {
        display: false,   // we use our own legend checkboxes
      },
      tooltip: {
        backgroundColor: 'rgba(17, 24, 39, 0.92)',
        titleFont: { size: 12, weight: 'bold' as const },
        bodyFont: { size: 11 },
        padding: { top: 8, bottom: 8, left: 12, right: 12 },
        cornerRadius: 8,
        displayColors: true,
        boxWidth: 10,
        boxHeight: 10,
        boxPadding: 4,
        callbacks: {
          title: (items: TooltipItem<'radar'>[]) => {
            if (!items.length) return '';
            const label = labels[items[0].dataIndex];
            return Array.isArray(label) ? label.join(' ') : label;
          },
          afterTitle: (items: TooltipItem<'radar'>[]) => {
            if (!items.length) return '';
            const dimKey = RADAR_DIMENSION_KEYS[items[0].dataIndex] as RadarDimensionKey;
            const meta = getRadarDimensionTooltipMeta(dimKey);
            return `${meta.summary}\nStandards: ${meta.onetMapping} / ${meta.shrmMapping}`;
          },
          label: (item: TooltipItem<'radar'>) => {
            const dsLabel = item.dataset.label || '';
            const val = Math.round(item.raw as number);
            if (dsLabel === 'Job Requirements') {
              const dimKey = RADAR_DIMENSION_KEYS[item.dataIndex] as RadarDimensionKey;
              const rawWeight = dimensionWeights[dimKey] ?? 0;
              return `  ${dsLabel}: ${val} (weight: ${Math.round(rawWeight * 100)}%)`;
            }
            return `  ${dsLabel}: ${val}/100`;
          },
        },
      },
    },
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    elements: {
      line: { tension: 0.1 },
    },
    animation: false as const,
  };

  const handleCopyChart = useCallback(async () => {
    const chart = chartRef.current;
    if (!chart) return;
    const canvas = chart.canvas as HTMLCanvasElement;
    try {
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
      if (blob) {
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      }
    } catch {
      // Fallback: copy as data URL text
      const dataUrl = canvas.toDataURL('image/png');
      await navigator.clipboard.writeText(dataUrl);
    }
  }, []);

  if (!dimensionWeights || Object.keys(dimensionWeights).length === 0) return null;

  return (
    <div style={{ marginBottom: 'var(--space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-3)' }}>
        <h2 style={{ margin: 0, fontSize: 'var(--font-size-base)' }}>Dimension Analysis</h2>
        {showCopyAction && (
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <button
              className="v2-btn v2-btn-ghost"
              onClick={handleCopyChart}
              title="Copy chart to clipboard"
              style={{ fontSize: 'var(--font-size-xs)', padding: '4px 10px' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4, verticalAlign: 'middle' }}><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
              Copy Chart
            </button>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {showCandidateControls && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-2)',
            flex: '0 0 250px',
            width: '100%',
            maxWidth: 270,
          }}>
            {/* JD reference label */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', marginBottom: 4 }}>
              <span style={{
                display: 'inline-block', width: 18, height: 12,
                backgroundColor: 'rgba(239, 68, 68, 0.10)',
                border: '1.5px dashed rgba(239, 68, 68, 0.45)',
                borderRadius: 2,
              }} />
              Target Profile (shaded zone = JD priorities)
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 4 }}>
              <button
                className="v2-btn v2-btn-ghost"
                onClick={selectAll}
                style={{ fontSize: 11, padding: '2px 8px' }}
                disabled={selectedIndices.size >= Math.min(5, candidates.length)}
              >
                Show Top {Math.min(5, candidates.length)}
              </button>
              <button
                className="v2-btn v2-btn-ghost"
                onClick={clearAll}
                style={{ fontSize: 11, padding: '2px 8px' }}
                disabled={selectedIndices.size === 0}
              >
                Clear All
              </button>
            </div>

            {/* Candidate checkboxes */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {sortedCandidates.map((c, i) => {
                const checked = selectedIndices.has(i);
                // Determine which color this candidate WOULD get
                let assignedColor = 'rgba(107, 114, 128, 0.4)';
                if (checked) {
                  let ci = 0;
                  for (let j = 0; j <= i; j++) {
                    if (selectedIndices.has(j)) {
                      if (j === i) { assignedColor = CANDIDATE_COLORS[ci % CANDIDATE_COLORS.length].border; break; }
                      ci++;
                    }
                  }
                }
                const disabled = !checked && selectedIndices.size >= 5;
                return (
                  <label
                    key={c.resumeFilename}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      fontSize: 'var(--font-size-xs)',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      opacity: disabled ? 0.45 : 1,
                      padding: '3px 0',
                    }}
                      title={`${displayName(c.resumeFilename)} — Score: ${Math.round(c.score)}%`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleCandidate(i)}
                      disabled={disabled}
                      style={{ accentColor: assignedColor, cursor: disabled ? 'not-allowed' : 'pointer' }}
                    />
                    <span style={{
                      display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
                      backgroundColor: assignedColor,
                      flexShrink: 0,
                    }} />
                    <span title={displayName(c.resumeFilename)} style={{ maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>
                      {displayName(c.resumeFilename)}
                    </span>
                    <span style={{ fontWeight: 600, color: 'var(--text-primary)', marginLeft: 2 }}>{Math.round(c.score)}%</span>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        <div style={{ position: 'relative', height: 380, flex: '1 1 520px', minWidth: 280 }}>
          <Radar ref={chartRef} data={chartData} options={options} />
        </div>
      </div>
    </div>
  );
};

export default DimensionRadarChart;
